import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { ViewToggle } from '../view/ViewToggle';
import { FolderPlus, Plus, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { FolderDetails } from '@/types/hierarchicalFolders';

interface DashboardHeaderProps {
  currentFolder?: FolderDetails | null;
  onNewFolderClick?: () => void;
  onNewVideoClick?: () => void;
  onAddVideosClick?: () => void;
  isReadOnly?: boolean;
}

export function DashboardHeader({ 
  currentFolder, 
  onNewFolderClick, 
  onNewVideoClick,
  onAddVideosClick,
  isReadOnly = false
}: DashboardHeaderProps) {
  useLanguage();
  return (
    <div className="mb-6">
      {/* Desktop: everything in one row */}
      <div className="hidden lg:flex justify-between items-center">
        {currentFolder && (
          <div className="flex items-center gap-2 text-sm">
            <Link to="/meus-videos" className="text-muted-foreground hover:text-foreground transition-colors">
              {translateInterfaceText("Meus Vídeos")}</Link>
            {currentFolder.breadcrumb.map(folder => (
              <>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                <Link 
                  to={`/meus-videos?folderId=${folder.id}`}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  {folder.name}
                </Link>
              </>
            ))}
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
            <span className="font-semibold text-foreground">{currentFolder.name}</span>
          </div>
        )}
        
        <div className="flex items-center gap-3 ml-auto">
          <ViewToggle />
          
          {!isReadOnly && (
            <Button
              variant="outline"
              onClick={onNewFolderClick}
              className="gap-2 min-h-[44px]"
            >
              <FolderPlus className="h-4 w-4" />
              {translateInterfaceText("Nova Pasta")}</Button>
          )}
          
          {!isReadOnly && currentFolder && onAddVideosClick && (
          <Button
            variant="outline"
            onClick={onAddVideosClick}
            className="gap-2 min-h-[44px]"
          >
            <Plus className="h-4 w-4" />
            {translateInterfaceText("Adicionar Vídeos")}</Button>
        )}

        {/* Só mostrar "Novo Vídeo" quando NÃO estiver dentro de uma pasta */}
        {!isReadOnly && !currentFolder && (
          <Button
            onClick={onNewVideoClick}
            className="gap-2 min-h-[44px]"
          >
            <Plus className="h-4 w-4" />
            {translateInterfaceText("Novo Vídeo")}</Button>
        )}
        </div>
      </div>
      
      {/* Mobile/Tablet: Compact Panda-style */}
      <div className="lg:hidden">
        {/* Título compacto - mt-2 mb-2 */}
        <div className="mb-2">
          {currentFolder ? (
            <div className="flex items-center gap-1 text-xs overflow-x-auto mb-1">
              <Link to="/meus-videos" className="text-muted-foreground hover:text-foreground whitespace-nowrap">
                {translateInterfaceText("Meus Vídeos")}</Link>
              {currentFolder.breadcrumb.map(folder => (
                <>
                  <ChevronRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                  <Link 
                    to={`/meus-videos?folderId=${folder.id}`}
                    className="text-muted-foreground hover:text-foreground whitespace-nowrap"
                  >
                    {folder.name}
                  </Link>
                </>
              ))}
              <ChevronRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />
              <span className="font-semibold text-foreground whitespace-nowrap">{currentFolder.name}</span>
            </div>
          ) : (
            <>
              <h1 className="text-[22px] leading-[28px] font-semibold text-foreground">
                {translateInterfaceText("Meus Vídeos")}</h1>
              <p className="text-[14px] leading-[20px] text-muted-foreground line-clamp-2 mt-1">
                {translateInterfaceText("Gerencie e visualize seus últimos vídeos")}</p>
            </>
          )}
        </div>
        
        {/* Toolbar compacta - mb-3 */}
        <div className="bg-card rounded-xl shadow-sm p-3 space-y-3 mb-3">
          {/* Segmented control 50/50 */}
          <ViewToggle />
          
          {/* Botões empilhados - largura total */}
        {/* Botões Novo Vídeo e Nova Pasta - lado a lado */}
        <div className="flex gap-2">
          {!isReadOnly && (
            <Button
              variant="outline"
              onClick={onNewFolderClick}
              className="flex-1 gap-2 py-2.5"
            >
              <FolderPlus className="h-4 w-4" />
              {translateInterfaceText("Nova Pasta")}</Button>
          )}
          
          {/* Só mostrar "Novo Vídeo" quando NÃO estiver dentro de uma pasta */}
          {!isReadOnly && !currentFolder && (
            <Button
              onClick={onNewVideoClick}
              className="flex-1 gap-2 py-2.5"
            >
              <Plus className="h-4 w-4" />
              {translateInterfaceText("Novo Vídeo")}</Button>
          )}
        </div>
          {!isReadOnly && currentFolder && onAddVideosClick && (
            <Button 
              onClick={onAddVideosClick}
              variant="outline" 
              className="w-full h-11 justify-center gap-2"
            >
              <Plus className="h-5 w-5" />
              {translateInterfaceText("Adicionar Vídeos")}</Button>
          )}
        </div>
      </div>
    </div>
  );
}